import {
  List, Datagrid, TextField, BooleanField, EditButton, Edit, Create, SimpleForm,
  TextInput, BooleanInput, useRecordContext, required, AutocompleteInput, useGetList,
} from 'react-admin';
import { Stack, Typography, Chip, Box } from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import { useWatch } from 'react-hook-form';
import { ImageUploadInput } from '../../components/ImageUploadInput.jsx';
import { TiptapInput } from '../../components/TiptapInput.jsx';
import { tokens } from '../../theme.js';

/**
 * Free-form autocomplete that suggests existing `parentGroup` values from
 * the Pages list (so an admin doesn't accidentally create "Academics" and
 * "academics" as two different groups).
 */
function ParentGroupSelect() {
  const { data } = useGetList('pages', {
    pagination: { page: 1, perPage: 100 },
    sort: { field: 'title', order: 'ASC' },
  });
  const choices = Array.from(new Set((data || []).map((p) => p.parentGroup).filter(Boolean)))
    .map((g) => ({ id: g, name: g }));
  return (
    <AutocompleteInput
      source="parentGroup"
      choices={choices}
      onCreate={(name) => ({ id: name, name })}
      label="Parent group (breadcrumb)"
      helperText='Groups this page under a breadcrumb parent. E.g. "Academics", "University Placement", "About".'
      fullWidth
    />
  );
}

/**
 * Live preview of the portrait with the optional gold corner accent, so the
 * admin can see the "decorative frame" before saving. Reads the live form
 * values (not the saved record) so it updates as the toggle flips.
 */
function ImageFramePreview() {
  const portraitImage = useWatch({ name: 'portraitImage' });
  const framed = useWatch({ name: 'heroImageFrame' });
  if (!portraitImage) return null;
  return (
    <Box sx={{ mb: 1 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        Portrait preview {framed ? '(with gold corner frame)' : ''}
      </Typography>
      <Box sx={{ position: 'relative', width: 150, mt: 1 }}>
        <Box
          component="img"
          src={portraitImage}
          alt=""
          sx={{ display: 'block', width: '100%', aspectRatio: '4 / 5', objectFit: 'cover', borderRadius: 0.5 }}
        />
        {framed && (
          <Box
            aria-hidden
            sx={{
              position: 'absolute', top: -7, right: -7, width: '64%', height: '32%',
              borderTop: `1px solid ${tokens.gold}`,
              borderRight: `1px solid ${tokens.gold}`,
              pointerEvents: 'none',
            }}
          />
        )}
      </Box>
    </Box>
  );
}

const filters = [
  <TextInput key="q" source="q" label="Search title/slug" alwaysOn />,
];

const PageList = () => (
  <List sort={{ field: 'title', order: 'ASC' }} filters={filters} perPage={50}>
    <Datagrid rowClick="edit" bulkActionButtons={false}>
      <TextField source="title" />
      <TextField source="slug" />
      <TextField source="template" label="Layout" />
      <BooleanField source="isPublished" label="Published" />
      <EditButton />
    </Datagrid>
  </List>
);

const PageForm = ({ isCreate }) => {
  const record = useRecordContext();
  const slug = record?.slug;
  const template = record?.template || 'content';
  // Content pages are free-form (body, breadcrumbs, portrait caption). The
  // other templates are fixed-layout "system" pages — the admin only controls
  // their header, banner and SEO; their layout + content come from code/data.
  const isContent = isCreate || template === 'content';

  return (
    <Stack spacing={2} sx={{ maxWidth: 900 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
        <TextInput
          source="title"
          validate={required()}
          fullWidth
          helperText="Used in menus, breadcrumbs and the browser tab."
        />
        <BooleanInput source="isPublished" defaultValue={true} />
      </Stack>

      {isCreate ? (
        <TextInput
          source="slug"
          label="URL slug (optional)"
          helperText="Leave blank to auto-generate from the title"
          fullWidth
        />
      ) : (
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <Typography variant="body2" color="text.secondary">URL slug:</Typography>
          <Chip label={slug} size="small" sx={{ fontFamily: 'monospace' }} />
          <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>Layout:</Typography>
          <Chip label={template} size="small" color="primary" variant="outlined" />
          {!isContent && (
            <Typography variant="caption" color="text.secondary">
              (system page — fixed layout; you control its header, banner &amp; SEO here)
            </Typography>
          )}
        </Stack>
      )}

      <Typography variant="overline" color="text.secondary" sx={{ pt: 1 }}>Header</Typography>
      <TextInput
        source="headline"
        label="Headline (big heading)"
        fullWidth
        helperText={'The large heading on the page. Wrap words in *asterisks* for the gold italic accent — e.g. "Spaces built for *learning*". Leave blank to use the title above.'}
      />
      <TextInput source="lede" label="Lede / intro paragraph" multiline minRows={2} fullWidth />

      <Typography variant="overline" color="text.secondary" sx={{ pt: 1 }}>Banner image</Typography>
      <ImageUploadInput source="heroImage" label="Banner / hero image" />

      {isContent && (
        <>
          <Typography variant="overline" color="text.secondary" sx={{ pt: 1 }}>
            Portrait &amp; two-column layout (optional)
          </Typography>
          <ImageUploadInput source="portraitImage" label="Portrait image" />
          <BooleanInput
            source="heroImageFrame"
            label="Add decorative gold corner frame to the portrait"
            helperText="Optional. Draws a thin gold corner accent over the portrait (same style as the leadership portraits). Only shows when a portrait image is set."
          />
          <ImageFramePreview />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextInput
              source="imageCaptionTitle"
              label="Caption — title (bold)"
              fullWidth
              helperText='Optional. Bold first line under the portrait, e.g. "The Principal".'
            />
            <TextInput
              source="imageCaptionSubtitle"
              label="Caption — subtitle"
              fullWidth
              helperText='Optional second line, e.g. "Maple Leaf International School".'
            />
          </Stack>
          <Box sx={{ background: 'var(--cream, #faf6ec)', border: '1px dashed var(--line, #e6e1d4)', borderRadius: 1, p: 1.5 }}>
            <Typography variant="caption" sx={{ color: 'var(--ink-3, #5d6660)' }}>
              Add a <strong>portrait image</strong> to show this page in a <strong>two-column layout</strong>
              (portrait + caption on the left, your body text on the right). Leave it empty for a normal
              full-width page. This is separate from the <strong>banner</strong> set above.
            </Typography>
          </Box>

          <Typography variant="overline" color="text.secondary" sx={{ pt: 1 }}>Breadcrumbs</Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <ParentGroupSelect />
            <TextInput
              source="parentLink"
              label="Parent link (optional)"
              helperText='Makes the parent crumb clickable. E.g. "/about". Leave blank for a plain text crumb.'
              fullWidth
            />
          </Stack>
          <Box sx={{ background: 'var(--cream, #faf6ec)', border: '1px dashed var(--line, #e6e1d4)', borderRadius: 1, p: 1.5 }}>
            <Typography variant="caption" sx={{ color: 'var(--ink-3, #5d6660)' }}>
              <strong>Breadcrumbs:</strong> with a parent group set, the public page shows <em>Home / Parent group / This page</em>. Without one, it shows just <em>Home / This page</em>.
            </Typography>
          </Box>

          <Typography variant="overline" color="text.secondary" sx={{ pt: 1 }}>Body</Typography>
          <TiptapInput source="body" label="" />
        </>
      )}

      <Typography variant="overline" color="text.secondary" sx={{ pt: 1 }}>SEO</Typography>
      <TextInput source="metaDescription" label="Meta description (SEO)" multiline minRows={2} fullWidth />
    </Stack>
  );
};

const PageEdit = () => (
  <Edit redirect="list" mutationMode="pessimistic">
    <SimpleForm><PageForm /></SimpleForm>
  </Edit>
);
const PageCreate = () => (
  <Create redirect="list">
    <SimpleForm><PageForm isCreate /></SimpleForm>
  </Create>
);

export default {
  list: PageList,
  edit: PageEdit,
  create: PageCreate,
  icon: DescriptionIcon,
  options: { label: 'Pages' },
  recordRepresentation: 'title',
};
